import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { 
    Handshake, Trash2, Edit, ChevronLeft, 
    Plus, Save, AlignLeft, Image as ImageIcon
} from "lucide-react";
import { useSearchParams } from "react-router-dom";

// --- WSPÓŁDZIELONE STYLE ---
const ManagerContainer = styled.div` width: 100%; `;
const HeaderRow = styled.div` display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; h2 { margin: 0; font-size: 1.8rem; color: #111; font-weight: 800; } `;
const PrimaryButton = styled.button` display: flex; align-items: center; gap: 8px; background: #111; color: #fff; padding: 10px 18px; border: none; border-radius: 8px; font-weight: 700; cursor: pointer; transition: all 0.3s; &:hover { background: #333; transform: translateY(-2px); } &:disabled { opacity: 0.6; cursor: not-allowed; transform: none; } `;
const SecondaryButton = styled.button` display: flex; align-items: center; gap: 8px; background: #fff; color: #111; padding: 10px 18px; border: 1px solid #ddd; border-radius: 8px; font-weight: 600; cursor: pointer; transition: all 0.3s; &:hover { background: #f4f6f8; } `;

// --- FORMULARZ ---
const FormCard = styled.form` background: #fff; border-radius: 12px; padding: 30px; border: 1px solid #eee; box-shadow: 0 4px 15px rgba(0,0,0,0.03); margin-bottom: 30px; `;
const FormSectionTitle = styled.h4` margin: 0 0 20px 0; font-size: 1.1rem; color: #111; border-bottom: 2px solid #f0f0f0; padding-bottom: 10px; display: flex; align-items: center; gap: 10px; svg { color: #888; } `;
const FormGroup = styled.div` display: flex; flex-direction: column; gap: 8px; margin-bottom: 20px; label { font-size: 0.9rem; font-weight: 700; color: #555; } input { padding: 12px 14px; border: 1px solid #ddd; border-radius: 8px; font-size: 1rem; font-family: inherit; transition: border-color 0.3s; &:focus { outline: none; border-color: #111; } } `;

// --- WIDOK KAFELKÓW (SIATKA SPONSORÓW) ---
const Grid = styled.div` display: grid; grid-template-columns: repeat(auto-fill, minmax(250px, 1fr)); gap: 20px; `;
const SponsorCard = styled.div` background: #fff; border-radius: 12px; border: 1px solid #eee; box-shadow: 0 4px 15px rgba(0,0,0,0.03); display: flex; flex-direction: column; overflow: hidden; transition: transform 0.3s ease; &:hover { transform: translateY(-3px); box-shadow: 0 8px 25px rgba(0,0,0,0.06); } .image-wrapper { width: 100%; height: 150px; background: #f4f6f8; display: flex; align-items: center; justify-content: center; overflow: hidden; padding: 20px; box-sizing: border-box; img { max-width: 100%; max-height: 100%; object-fit: contain; } svg { color: #ccc; width: 40px; height: 40px; } } .content { padding: 20px; text-align: center; h3 { margin: 0; font-size: 1.2rem; font-weight: 800; color: #111; } } .actions { display: flex; border-top: 1px solid #f0f0f0; background: #fafafa; button { flex: 1; display: flex; justify-content: center; align-items: center; gap: 5px; padding: 15px; border: none; font-weight: 600; cursor: pointer; transition: 0.2s; background: transparent; } .btn-edit { color: #234ffc; border-right: 1px solid #f0f0f0; } .btn-edit:hover { background: #eef2ff; } .btn-delete { color: #dc3545; } .btn-delete:hover { background: #fff0f0; } } `;

export default function SponsorsManager() {
    const [sponsors, setSponsors] = useState([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const viewState = searchParams.get("action") || "list";
    const editingId = searchParams.get("id") || null;

    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({ nazwa: "", logo: "" });
    const [imageFile, setImageFile] = useState(null); 
    
    const dataLoadedRef = useRef(null);
    const fileInputRef = useRef(null);

    const adresAPI = "https://api.pth-hokej.pl";

    const fetchSponsors = async () => {
        try {
            const res = await fetch(`${adresAPI}/api/sponsors`);
            const data = await res.json();
            setSponsors(data);
        } catch (err) { console.error("Błąd pobierania sponsorów:", err); }
    };

    useEffect(() => { fetchSponsors(); }, []);

    useEffect(() => {
        if (viewState === "edit" && editingId && sponsors.length > 0) {
            if (dataLoadedRef.current !== editingId) {
                const item = sponsors.find(e => e._id === editingId);
                if (item) {
                    setFormData({ nazwa: item.nazwa || "", logo: item.logo || "" });
                    setImageFile(null); 
                    if (fileInputRef.current) fileInputRef.current.value = "";
                    dataLoadedRef.current = editingId;
                }
            }
        } else if (viewState === "list") {
            dataLoadedRef.current = null;
        }
    }, [viewState, editingId, sponsors]);

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleRemoveImage = () => {
        setImageFile(null);
        setFormData((prev) => ({ ...prev, logo: "" }));
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const url = viewState === "edit" ? `${adresAPI}/api/sponsors/${editingId}` : `${adresAPI}/api/sponsors`;
            const method = viewState === "edit" ? "PUT" : "POST";

            const submitData = new FormData();
            submitData.append("nazwa", formData.nazwa);
            
            if (imageFile) submitData.append("logoPlik", imageFile);
            else if (formData.logo) submitData.append("logo", formData.logo);
            else submitData.append("logo", "");

            await fetch(url, { method, body: submitData });
            await fetchSponsors();
            setSearchParams({});
        } catch (err) { console.error(err); } 
        finally { setLoading(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Na pewno chcesz bezpowrotnie usunąć tego sponsora?")) return;
        try {
            await fetch(`${adresAPI}/api/sponsors/${id}`, { method: "DELETE" });
            fetchSponsors();
        } catch (err) { console.error(err); }
    };

    const handleAddClick = () => {
        setFormData({ nazwa: "", logo: "" });
        setImageFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        setSearchParams({ action: "add" });
    };

    const handleEditClick = (item) => {
        setFormData({ nazwa: item.nazwa || "", logo: item.logo || "" });
        setImageFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        dataLoadedRef.current = item._id;
        setSearchParams({ action: "edit", id: item._id });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <ManagerContainer>
            {viewState === "list" && (
                <>
                    <HeaderRow>
                        <h2>Sponsorzy i Partnerzy</h2>
                        <PrimaryButton onClick={handleAddClick}><Plus size={20} /> Dodaj Sponsora</PrimaryButton>
                    </HeaderRow>
                    {sponsors.length === 0 ? (
                        <p style={{ color: "#666" }}>Brak dodanych sponsorów w systemie.</p>
                    ) : (
                        <Grid>
                            {sponsors.map((sp) => (
                                <SponsorCard key={sp._id}>
                                    <div className="image-wrapper">
                                        {sp.logo ? <img src={sp.logo} alt={sp.nazwa} /> : <Handshake />}
                                    </div>
                                    <div className="content">
                                        <h3>{sp.nazwa}</h3>
                                    </div>
                                    <div className="actions">
                                        <button className="btn-edit" onClick={() => handleEditClick(sp)}><Edit size={16} /> Edytuj</button>
                                        <button className="btn-delete" onClick={() => handleDelete(sp._id)}><Trash2 size={16} /> Usuń</button>
                                    </div>
                                </SponsorCard>
                            ))}
                        </Grid>
                    )}
                </>
            )}

            {(viewState === "add" || viewState === "edit") && (
                <>
                    <HeaderRow>
                        <SecondaryButton onClick={() => setSearchParams({})}><ChevronLeft size={20} /> Wróć do listy</SecondaryButton>
                        <h2>{viewState === "edit" ? "Edytuj Sponsora" : "Nowy Sponsor"}</h2>
                    </HeaderRow>

                    <FormCard onSubmit={handleSubmit}>
                        <FormSectionTitle><AlignLeft size={20}/> Informacje o firmie / partnerze</FormSectionTitle>
                        <FormGroup style={{ marginBottom: '30px' }}>
                            <label>Nazwa sponsora</label>
                            <input required name="nazwa" placeholder="np. Firma ABC" value={formData.nazwa} onChange={handleChange} />
                        </FormGroup>

                        <FormSectionTitle><ImageIcon size={20} /> Logo (opcjonalne, ale zalecane)</FormSectionTitle>
                        <FormGroup style={{ background: '#fafafa', padding: '20px', borderRadius: '8px', border: '1px solid #eee' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                <label style={{ margin: 0 }}>Wybierz źródło pliku</label>
                                {(imageFile || formData.logo) && (
                                    <button type="button" onClick={handleRemoveImage} style={{ display: 'flex', alignItems: 'center', gap: '5px', background: '#ffe0e0', color: '#dc3545', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem' }}>
                                        <Trash2 size={16} /> Usuń logo
                                    </button>
                                )}
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', alignItems: 'center' }}>
                                <div>
                                    <span style={{ fontSize: '0.85rem', color: '#888', display: 'block', marginBottom: '8px' }}>Opcja 1: Wgraj logo z komputera</span>
                                    <input type="file" accept="image/*" ref={fileInputRef} onChange={(e) => setImageFile(e.target.files[0])} style={{ padding: '8px', background: '#fff', width: '100%', boxSizing: 'border-box' }} disabled={!!formData.logo} />
                                </div>
                                <div>
                                    <span style={{ fontSize: '0.85rem', color: '#888', display: 'block', marginBottom: '8px' }}>Opcja 2: Wklej link do logo</span>
                                    <input name="logo" value={formData.logo} onChange={handleChange} placeholder="https://..." disabled={!!imageFile} style={{ width: '100%', boxSizing: 'border-box' }} />
                                </div>
                            </div>
                        </FormGroup>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
                            <PrimaryButton type="submit" disabled={loading}>
                                <Save size={20} /> {viewState === "edit" ? "Zapisz Zmiany" : "Dodaj Sponsora"}
                            </PrimaryButton>
                        </div>
                    </FormCard>
                </>
            )}
        </ManagerContainer>
    );
}